import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { TestEmailSend } from '@/client/campaigns/types/campaigns.type';
import { EMAIL_REGEX, LoadingStatuses } from '@/common/constants';
import { FormikInput } from '@/components/form';
import { useNotifications } from '@/hooks/notifications.hook';
import { useSendTestEmail } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { getPhishingLanguageLabel } from '@/utils/helpers';
import { preventDropdownBrowserTranslate } from '@/utils/helpers/ui.helper';
import { AxiosError } from 'axios';
import { Field, Form, Formik } from 'formik';
import { Dropdown } from 'primereact/dropdown';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { object, string } from 'yup';

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--default-padding-small);
`;

const NameRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--small-padding);
`;

type SendTestEmailFormProps = {
  campaignId: string;
  emailTemplateId: string;
  sendingProfileId: string;
  landingPageId: string;
  // The language versions of a language group, in planner order. With more
  // than one, the admin picks which version the test email uses.
  testSends?: TestEmailSend[];
  onSuccess?: () => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
};

type FormValues = {
  email: string;
  firstName: string;
  lastName: string;
};

export const SendTestEmailForm: React.FC<SendTestEmailFormProps> = ({
  campaignId,
  emailTemplateId,
  sendingProfileId,
  landingPageId,
  testSends,
  onSuccess,
  onCancel,
  showCancelButton = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const currentUser = useAppSelector(selectCurrentUser);
  const sendTestEmail = useSendTestEmail();
  const [isSending, setIsSending] = useState(false);
  const [selectedSendId, setSelectedSendId] = useState(
    testSends?.[0]?.campaignId,
  );

  useEffect(() => {
    setSelectedSendId(testSends?.[0]?.campaignId);
  }, [testSends?.map((send) => send.campaignId).join(',')]);

  const showLanguagePicker = (testSends?.length ?? 0) > 1;
  const selectedSend = showLanguagePicker
    ? testSends?.find((send) => send.campaignId === selectedSendId)
    : undefined;
  const languageOptions = useMemo(
    () =>
      (testSends ?? []).map((send) => {
        const label = getPhishingLanguageLabel(send.language);

        return {
          label: send.isFallback
            ? t('simulations.testEmail.fallbackLanguage', { language: label })
            : label,
          value: send.campaignId,
        };
      }),
    [testSends, t],
  );

  const {
    lastMessage,
    setNotificationParam: setJobId,
    notificationParam: jobId,
  } = useNotifications(client.senderProfiles.senderProfilesJobNotifyUrl);

  useEffect(() => {
    if (!jobId) return;

    if (
      !lastMessage?.data?.event ||
      ![
        'sending-profile.email-sent',
        'sending-profile.email-not-sent',
      ].includes(lastMessage?.data?.event)
    )
      return;

    setIsSending(false);
    setJobId(undefined);

    if (lastMessage?.data?.event === 'sending-profile.email-sent') {
      toast?.success(t('toast.success'), t('campaign.testEmail.sent'), 5000);
      onSuccess?.();
    }
    if (lastMessage?.data?.event === 'sending-profile.email-not-sent') {
      toast?.info(t('toast.info'), t('campaign.testEmail.notSent'), 5000);
    }
  }, [JSON.stringify(lastMessage)]);

  const validationSchema = useMemo(
    () =>
      object().shape({
        email: string()
          .required(t('error.requiredField'))
          .matches(EMAIL_REGEX, t('error.invalidEmail')),
        firstName: string().required(t('error.requiredField')),
        lastName: string().nullable(),
      }),
    [t],
  );

  const initialValues: FormValues = {
    email: currentUser?.email ?? '',
    firstName: currentUser?.firstName ?? '',
    lastName: currentUser?.lastName ?? '',
  };

  const handleSubmit = async (values: FormValues) => {
    setIsSending(true);
    try {
      const response = await sendTestEmail.send({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName || undefined,
        campaignId: selectedSend?.campaignId ?? campaignId,
        emailTemplateId: selectedSend?.emailTemplateId ?? emailTemplateId,
        sendingProfileId: selectedSend?.sendingProfileId ?? sendingProfileId,
        landingPageId: selectedSend?.landingPageId ?? landingPageId,
      });

      setJobId(response.jobId);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
      setIsSending(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnChange={false}
    >
      {({ isValid, dirty, submitForm }) => (
        <Form>
          <FormContainer>
            {showLanguagePicker && (
              <div className="field w-full mb-0">
                <label htmlFor="test-email-language">{t('generic.lang')}</label>
                <Dropdown
                  inputId="test-email-language"
                  className="w-full"
                  dropdownIcon="pi pi-chevron-down"
                  value={selectedSendId}
                  options={languageOptions}
                  onChange={(e) => setSelectedSendId(e.value)}
                  disabled={isSending}
                  {...preventDropdownBrowserTranslate}
                />
              </div>
            )}
            <Field
              name="email"
              label={t('simulations.testEmail.email')}
              component={FormikInput}
              className="w-full"
              required
            />
            <NameRow>
              <div>
                <Field
                  name="firstName"
                  label={t('simulations.testEmail.firstName')}
                  component={FormikInput}
                  className="w-full"
                  required
                />
              </div>
              <div>
                <Field
                  name="lastName"
                  label={t('simulations.testEmail.lastName')}
                  component={FormikInput}
                  className="w-full"
                />
              </div>
            </NameRow>
            <div style={{ display: 'flex', gap: 'var(--small-padding)' }}>
              <AppButton
                label={t('simulations.testEmail.send')}
                severity={showCancelButton ? undefined : 'secondary'}
                type={showCancelButton ? undefined : 'outlined'}
                onClick={submitForm}
                isDisabled={
                  isSending || !isValid || (!dirty && !initialValues.email)
                }
                state={
                  isSending ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
                }
              />
              {showCancelButton && (
                <AppButton
                  severity="secondary"
                  type="outlined"
                  label={t('simulations.testEmail.cancel')}
                  onClick={onCancel}
                />
              )}
            </div>
          </FormContainer>
        </Form>
      )}
    </Formik>
  );
};
